'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useApolloClient } from '@apollo/client';
import {
  GetProjectFieldDefinitions,
  GetRootCollectionForProject,
  useUpdateCollectionFieldsCardMutation,
} from '@frameio/graphql-client/generated';
import { ToastAlert, ToastButton, useToast } from '@frameio/vapor';
import TEXT from './generated/TEXT/useVariationsProcessingToast';

TEXT.DEFINE({
  /** This text describes assets on which Firefly actions were applied and their variations are being generated. */
  variationsProcessing: 'Variations are being processed',
  /** This button is used to navigate to the folder and also see the notes. */
  viewFolder: 'View folder and Notes',
});

type ShowToastOptions = {
  href: string;
  projectId?: string;
};

type FieldNode = {
  fieldDefinition?: { id: string } | null;
  isFrozen?: boolean | null;
  isVisible?: boolean | null;
  width?: number | null;
} | null;

function toFieldsInput(nodes: FieldNode[]) {
  return (nodes ?? [])
    .map((f) =>
      f?.fieldDefinition?.id
        ? {
            fieldDefinitionId: f.fieldDefinition.id,
            isFrozen: Boolean(f.isFrozen),
            isVisible: Boolean(f.isVisible),
            width: f?.width ?? null,
          }
        : undefined
    )
    .filter((x): x is NonNullable<typeof x> => Boolean(x));
}

export function useVariationsProcessingToast() {
  const { addToast } = useToast();
  const router = useRouter();
  const client = useApolloClient();
  const [updateFieldsCard] = useUpdateCollectionFieldsCardMutation();

  const ensureNotesVisible = useCallback(
    async (activeProjectId: string) => {
      const defs = await client.query({
        query: GetProjectFieldDefinitions,
        variables: {
          projectId: activeProjectId,
          excludeSystemFields: false,
        },
      });

      const notesDef =
        defs?.data?.account?.project?.fieldDefinitions?.nodes?.find(
          (d) => d?.name === 'Notes'
        );
      if (!notesDef) return;

      const root = await client.query({
        query: GetRootCollectionForProject,
        variables: { projectId: activeProjectId },
        fetchPolicy: 'network-only',
      });

      const viewerRoot = root?.data?.account?.project?.viewerRootCollection;
      const collectionId = viewerRoot?.id;
      if (!collectionId) return;

      const cardNodes = viewerRoot?.fieldsCard?.nodes ?? [];

      const filteredCardNodes = cardNodes.filter(
        (f) =>
          f?.fieldDefinition?.id !== notesDef?.id &&
          f?.fieldDefinition?.name !== 'Notes'
      );

      const notesFieldNode: FieldNode = {
        fieldDefinition: { id: notesDef.id },
        isFrozen: false,
        isVisible: true,
        width: null,
      };

      const fieldsCardInput = toFieldsInput([
        ...filteredCardNodes,
        notesFieldNode,
      ]);

      await updateFieldsCard({
        variables: {
          collectionId,
          fieldsCard: fieldsCardInput,
        },
      });
    },
    [client, updateFieldsCard]
  );

  const handleViewFolderPress = useCallback(
    async ({
      href,
      projectId,
      close,
    }: ShowToastOptions & { close: () => void }) => {
      router.push(href);
      try {
        if (projectId) await ensureNotesVisible(projectId);
      } catch {
        // Ignore errors and still navigate
      } finally {
        close();
      }
    },
    [ensureNotesVisible, router]
  );

  return useCallback(
    ({ href, projectId }: ShowToastOptions) => {
      addToast((close, closeLabel) => {
        const onPress = () => {
          handleViewFolderPress({ href, projectId, close });
        };

        return (
          <ToastAlert
            adornmentAfter={
              <ToastButton onPress={onPress}>{TEXT.viewFolder}</ToastButton>
            }
            closeLabel={closeLabel}
            onRequestClose={close}
            title={TEXT.variationsProcessing}
            variant="success"
          />
        );
      });
    },
    [addToast, handleViewFolderPress]
  );
}
